import { ColumnInfo, ColumnSummaryType, ColumnType } from '@/Types';
import { DataGrid } from 'devextreme-react/data-grid';
import { dateTimeUtils, stringUtils } from '@/Utils';

export function getColumnRender(columnType: ColumnType, value: string): string | boolean | number {
  if (+value == 0 && columnType === ColumnType.Currency) return (0).toCurrencyString();
  // devexpress converts undefined to NaN string value
  if (value == 'NaN' && columnType === ColumnType.Currency) return (0).toCurrencyString();

  if (!value || value == 'NaN') return '';

  switch (columnType) {
    case ColumnType.Decimal:
      return Number(stringUtils.normalizeToNumberString(value)).toDecimalString();

    case ColumnType.Currency:
      return Number(stringUtils.normalizeToNumberString(value)).toCurrencyString();

    case ColumnType.Boolean:
      return Boolean(value);

    case ColumnType.Number:
      return Number(value).toIntegerString();

    case ColumnType.DateTime:
      return dateTimeUtils.utcStringToLocalString(value, false);

    case ColumnType.Date:
      return dateTimeUtils.utcStringToLocalString(value, true);

    default:
      return String(value || '');
  }
} // getColumnRender

export function getSelectedDataByKey<TData>(
  dataGrid: DataGrid<TData, keyof TData> | undefined,
  rowKey: keyof TData | '',
): TData[] {
  if (!dataGrid?.instance || !rowKey) return [];

  const selectedRowsData = dataGrid.instance.getSelectedRowsData();

  const sortedData = selectedRowsData.sort((a, b) => {
    const indexA = dataGrid.instance.getRowIndexByKey(a[rowKey] as any);
    const indexB = dataGrid.instance.getRowIndexByKey(b[rowKey] as any);
    return indexA - indexB;
  });

  return Array.from(new Set(sortedData));
} // getSelectedDataByKey

export const getCalculateDisplayValue = <TData extends object>(
  row: TData,
  columnType?: ColumnType,
  fieldName?: keyof TData,
) => {
  if (!fieldName) {
    return;
  }

  return row[fieldName];
}; // getCalculateCellValue

export const getTypeByColumnInfo = (columnType?: ColumnType) => {
  switch (columnType) {
    case ColumnType.String:
    case ColumnType.Currency:
    case ColumnType.Weight:
      return 'string';

    case ColumnType.DateTime:
      return 'datetime';

    case ColumnType.Date:
      return 'date';

    case ColumnType.Number:
      return 'number';

    case ColumnType.Boolean:
      return 'boolean';

    default:
      return 'string';
  }
}; // getTypeByColumnInfo

export function calculateSummary<TData extends object>(items: TData[], columnInfos: ColumnInfo<TData>[]): number[] {
  return columnInfos
    .map(({ fieldName, summary }) => {
      const values: any = fieldName ? items.map((item) => item[fieldName]) : [];

      switch (summary?.type) {
        case ColumnSummaryType.Sum:
          return values.reduce((acc: number, val: number) => acc + (val || 0), 0);

        case ColumnSummaryType.Min:
          return values.length ? Math.min(...values) : null;

        case ColumnSummaryType.Count:
          return items.length;

        default:
          return null;
      }
    })
    .filter((item) => item != null);
}

export const calculateFilterExpression = <TData extends object>(columnType?: ColumnType, fieldName?: keyof TData) => (
  value: any,
  operation: any,
) => {
  const allowedOps = ['=', '<>', '>', '<', '>=', '<=', 'contains', 'startswith', 'endswith'];

  if (!allowedOps.includes(operation)) {
    return [fieldName, 'contains', value]; // fallback for search panel
  }

  if (!value) {
    return [[fieldName, '=', null], 'or', [fieldName, '=', ''], 'or', [fieldName, '=', undefined]];
  }

  if (columnType === ColumnType.Date || columnType === ColumnType.DateTime) {
    const start = new Date(value);
    const end = new Date(start);

    if (columnType === ColumnType.Date) {
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() + 1);
    } else {
      end.setSeconds(end.getSeconds() + 1);
    }

    if (operation === '=') {
      return [[fieldName, '>=', start], 'and', [fieldName, '<', end]];
    }

    if (operation === '<>') {
      return [[fieldName, '<', start], 'or', [fieldName, '>=', end]];
    }
  }

  return [fieldName, operation, value];
};

export const getFilterValues = <TData extends object>(
  data: TData[],
  field?: keyof TData,
  columnType = ColumnType.String,
) => {
  if (!field) return;

  const items = new Set(
    data?.map((item) => {
      let value: any = item[field];

      if (columnType === ColumnType.Date) {
        value = String(value).split(' ')[0];
      }

      return value || '';
    }),
  );

  return [...items]
    ?.map((value) => {
      let text: any = value;

      if (columnType == ColumnType.Date) {
        text = dateTimeUtils.utcStringToLocalString(text, true);
      }

      if (columnType == ColumnType.DateTime) {
        text = dateTimeUtils.utcStringToLocalString(text, false);
      }

      return { text: text || '--', value: value };
    })
    .sort((a, b) => {
      if (!a.value) return -1;
      if (!b.value) return 1;
      return String(a.value)?.localeCompare(String(b.value));
    });
};
